import { useState } from 'react'
import { ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { relativeDate } from '../lib/format'
import { useApi } from '../lib/useApi'

/**
 * API keys and outbound webhooks (SW-7).
 *
 * **This screen is the whole feature.** Both backends have been complete and wired for weeks,
 * `/api/v1` authenticates keys and enforces the plan gate, and `dispatchOutbound` fires from
 * the restock fanout, the nightly job and the subscribe path. Neither had anywhere to be
 * switched on from, so Intelligence sold two things a merchant could pay for and never reach.
 *
 * Keys and signing secrets are shown **once**, at creation, and are unrecoverable afterwards,
 * only a hash is stored server-side. That is the right call and it is also the one thing a
 * merchant can permanently lose by clicking away, so the reveal is a banner that stays until
 * dismissed rather than a toast that disappears on its own.
 */

const TOPICS = ['subscription.created', 'restock.alerted', 'recommendation.updated'] as const
type Topic = (typeof TOPICS)[number]

type ApiKey = {
  key_prefix: string
  name: string
  created_at: number
  last_used_at: number | null
  revoked_at: number | null
}

type Webhook = {
  id: string
  url: string
  topics: string
  created_at: number
  disabled_at: number | null
  last_error: string | null
  failure_count: number
}

/** A secret the server will never show again. */
function RevealOnce({
  heading,
  value,
  onDismiss,
}: {
  heading: string
  value: string
  onDismiss: () => void
}) {
  return (
    <s-banner tone="warning" heading={heading}>
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Copy this now. It is stored only as a hash, so it cannot be shown again. If you lose it,
          revoke it and create another.
        </s-paragraph>
        <s-box padding="base" background="subdued" borderRadius="base">
          <s-text type="strong">{value}</s-text>
        </s-box>
        <s-stack direction="inline" gap="small-300">
          <s-button
            variant="primary"
            onClick={() => {
              navigator.clipboard?.writeText(value)
              shopify.toast.show('Copied')
            }}
          >
            Copy
          </s-button>
          <s-button variant="tertiary" onClick={onDismiss}>
            I have saved it
          </s-button>
        </s-stack>
      </s-stack>
    </s-banner>
  )
}

function ApiKeys() {
  const { data, error, loading, reload } = useApi<{ keys: ApiKey[] }>('/api-keys')
  const [name, setName] = useState('')
  const [created, setCreated] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState<unknown>(null)

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const keys = data?.keys ?? []
  const live = keys.filter((key) => key.revoked_at === null)

  async function create() {
    setBusy(true)
    setCreateError(null)
    try {
      const result = await api.post<{ key: string }>('/api-keys', {
        name: name.trim() || 'Untitled key',
      })
      setCreated(result.key)
      setName('')
      reload()
    } catch (err) {
      setCreateError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <s-section heading="API keys">
      <s-stack direction="block" gap="base">
        <s-paragraph color="subdued">
          Authenticate against <s-text type="strong">/api/v1</s-text> with{' '}
          <s-text type="strong">Authorization: Bearer &lt;key&gt;</s-text>. Read-only: waitlists,
          restock recommendations and metrics.
        </s-paragraph>

        {created ? (
          <RevealOnce
            heading="Your new API key"
            value={created}
            onDismiss={() => setCreated(null)}
          />
        ) : null}

        {createError ? <ErrorState error={createError} /> : null}

        <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
          <s-text-field
            label="Name"
            placeholder="e.g. Warehouse sync"
            value={name}
            onChange={(event: Event) => setName((event.target as HTMLInputElement).value)}
          />
          <s-button variant="primary" disabled={busy} onClick={create}>
            {busy ? 'Creating…' : 'Create key'}
          </s-button>
        </s-grid>

        {keys.length === 0 ? (
          <s-paragraph color="subdued">No keys yet.</s-paragraph>
        ) : (
          <s-table>
            <s-table-header-row>
              <s-table-header>Key</s-table-header>
              <s-table-header>Created</s-table-header>
              <s-table-header>Last used</s-table-header>
              <s-table-header />
            </s-table-header-row>
            <s-table-body>
              {keys.map((key) => (
                <s-table-row key={key.key_prefix}>
                  <s-table-cell>
                    <s-stack direction="block" gap="small-500">
                      <s-text type="strong">{key.name}</s-text>
                      <s-text color="subdued">{key.key_prefix}…</s-text>
                    </s-stack>
                  </s-table-cell>
                  <s-table-cell>{relativeDate(key.created_at)}</s-table-cell>
                  <s-table-cell>
                    {key.last_used_at === null ? (
                      // Distinguishes "issued and forgotten" from "in use"; which is the
                      // question you ask before revoking one.
                      <s-text color="subdued">Never</s-text>
                    ) : (
                      relativeDate(key.last_used_at)
                    )}
                  </s-table-cell>
                  <s-table-cell>
                    {key.revoked_at === null ? (
                      <s-button
                        variant="tertiary"
                        tone="critical"
                        onClick={async () => {
                          await api.del(`/api-keys/${key.key_prefix}`)
                          shopify.toast.show('Key revoked')
                          reload()
                        }}
                      >
                        Revoke
                      </s-button>
                    ) : (
                      <s-badge tone="neutral">Revoked</s-badge>
                    )}
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}

        {live.length > 0 ? (
          <s-text color="subdued">
            {live.length} active {live.length === 1 ? 'key' : 'keys'}.
          </s-text>
        ) : null}
      </s-stack>
    </s-section>
  )
}

function Webhooks() {
  const { data, error, loading, reload } = useApi<{ webhooks: Webhook[] }>('/outbound-webhooks')
  const [url, setUrl] = useState('')
  const [topics, setTopics] = useState<Topic[]>(['restock.alerted'])
  const [secret, setSecret] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState<unknown>(null)

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const webhooks = data?.webhooks ?? []
  const httpsProblem = url.length > 0 && !url.startsWith('https://')

  async function create() {
    setBusy(true)
    setCreateError(null)
    try {
      const result = await api.post<{ secret: string }>('/outbound-webhooks', {
        url: url.trim(),
        topics,
      })
      setSecret(result.secret)
      setUrl('')
      reload()
    } catch (err) {
      setCreateError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <s-section heading="Webhooks">
      <s-stack direction="block" gap="base">
        <s-paragraph color="subdued">
          We POST to your endpoint when these events happen. Each request is signed with the secret
          shown at creation. Verify it before trusting the payload.
        </s-paragraph>

        {secret ? (
          <RevealOnce
            heading="Your signing secret"
            value={secret}
            onDismiss={() => setSecret(null)}
          />
        ) : null}

        {createError ? <ErrorState error={createError} /> : null}

        <s-url-field
          label="Endpoint URL"
          placeholder="https://example.com/hooks/stockwait"
          value={url}
          error={httpsProblem ? 'Must start with https://' : undefined}
          onChange={(event: Event) => setUrl((event.target as HTMLInputElement).value)}
        />

        <s-stack direction="block" gap="small-400">
          <s-text type="strong">Events</s-text>
          {TOPICS.map((topic) => (
            <s-checkbox
              key={topic}
              label={topic}
              checked={topics.includes(topic)}
              onChange={() =>
                setTopics((current) =>
                  current.includes(topic)
                    ? current.filter((value) => value !== topic)
                    : [...current, topic],
                )
              }
            />
          ))}
        </s-stack>

        <s-stack direction="inline">
          <s-button
            variant="primary"
            // The server rejects both of these anyway; refusing here saves a round trip and
            // says why, rather than surfacing a schema error.
            disabled={busy || url.trim().length === 0 || httpsProblem || topics.length === 0}
            onClick={create}
          >
            {busy ? 'Adding…' : 'Add endpoint'}
          </s-button>
        </s-stack>

        {webhooks.length === 0 ? (
          <s-paragraph color="subdued">No endpoints yet.</s-paragraph>
        ) : (
          <s-table>
            <s-table-header-row>
              <s-table-header>Endpoint</s-table-header>
              <s-table-header>Events</s-table-header>
              <s-table-header>Status</s-table-header>
              <s-table-header />
            </s-table-header-row>
            <s-table-body>
              {webhooks.map((hook) => (
                <s-table-row key={hook.id}>
                  <s-table-cell>{hook.url}</s-table-cell>
                  <s-table-cell>{hook.topics.split(',').join(', ')}</s-table-cell>
                  <s-table-cell>
                    {hook.disabled_at !== null ? (
                      <s-stack direction="block" gap="small-500">
                        <s-badge tone="critical">Disabled</s-badge>
                        {/* Without the reason, "disabled" is a mystery the merchant opens a
                            support ticket about. */}
                        {hook.last_error ? (
                          <s-text color="subdued">{hook.last_error}</s-text>
                        ) : null}
                      </s-stack>
                    ) : hook.failure_count > 0 ? (
                      <s-badge tone="caution">{hook.failure_count} recent failures</s-badge>
                    ) : (
                      <s-badge tone="success">Active</s-badge>
                    )}
                  </s-table-cell>
                  <s-table-cell>
                    <s-button
                      variant="tertiary"
                      tone="critical"
                      onClick={async () => {
                        await api.del(`/outbound-webhooks/${hook.id}`)
                        shopify.toast.show('Endpoint removed')
                        reload()
                      }}
                    >
                      Remove
                    </s-button>
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </s-stack>
    </s-section>
  )
}

export function Developer() {
  return (
    <s-stack direction="block" gap="large">
      <ApiKeys />
      <Webhooks />
    </s-stack>
  )
}
